import { error, log } from '../app/logger.js';
import { animateUILoading } from '../ui/ui_loader.js';
import { addSwipeLeft, addSwipeRight, screenMedia } from '../util/util.js';
import { window } from '../window.js';
import { HTMLControl } from './html_control.js';

import './image_close_button.js';

/**
 * Image slider.
 */
class ImageSlider extends HTMLControl {
  constructor() {
    super();
    this.visibleItemCount = 0;

    this.addEventListener('click', ev => {
      if (ev.target.classList.contains('panel-gallery-pic')) {
        let offset = ev.offsetX < this.imgEl.width / 2 ? -1 : 1;
        this.setPicBy(offset, 'from-user-input');
      }
    });
    addSwipeLeft(this, () => this.setPicBy(1, 'fromUserInput'));
    addSwipeRight(this, () => this.setPicBy(-1, 'fromUserInput'));

    this.addEventListener('keydown', ev => {
      if (!this.imgEls) {
        return;
      }
      switch (ev.key) {
        case 'ArrowLeft':
          this.setPicBy(-1, 'from-user-input');
          break;
        case 'ArrowRight':
          this.setPicBy(1, 'from-user-input');
          break;
        case 'Escape':
          ev.stopPropagation();
          this.maximized ? this.minimize('notify') : this.close('notify');
          break;

        default:
          break;
      }
    });

    screenMedia.registerUI(() => this.refresh(), {
      orientation: true,
    });
  }

  static properties = {
    maximized: {
      attribute: 'boolean',
      set(v) {
        if (v) {
          this.maximizeButton.src = '/images/minimize-window.png';
          this.maximizeButton.setAttribute('title', 'Minimize');
        } else {
          this.maximizeButton.src = '/images/maximize-window.png';
          this.maximizeButton.setAttribute('title', 'Maximize');
        }
      },
    },
    sizezable: {
      set(v) {
        this.maximizeButton.hidden = !v;
      },
    },
  };

  /**
   * Returns index of a currently selected image.
   */
  get picIndex() {
    return this.#picIndex;
  }

  /**
   * Sets pictures and shows a picture at the given index.
   * Note: Pictures are given as a list of URLs or promises
   * resolved to URLs.
   */
  set(pics, picIdx) {
    log(
      `[control:image-slider] set to ${picIdx || 0} index and [${pics.join(
        ', '
      )}]`
    );
    this.#pics = pics;
    this.#picIndex = picIdx || 0;

    if (this.isInitialized) {
      this.setImpl();
    }
  }

  refresh() {
    const sliderWidth = this.getBoundingClientRect().width;
    if (sliderWidth == 0) {
      return;
    }

    const navitemw = this.navItemWidth;
    const navcontrolw = this.navControlWidth;

    let maxw = sliderWidth - navcontrolw * 2 - navitemw;
    this.navEl.style.width = `${maxw - (maxw % navitemw)}px`;

    this.visiblePicCount = Math.min(parseInt(maxw / navitemw), this.picCount);
    this.visiblePicCount < this.#pics.length
      ? this.classList.add('overflown')
      : this.classList.remove('overflown');

    this.scrollPicIntoView(this.#picIndex);

    // Preload pics asynchroniously: selected pic goes first, then all visible
    // pics.
    this.loadPicAt(this.#picIndex).then(() => this.loadVisiblePics());
  }

  initialize() {
    super.initialize();

    this.setAttribute('tabindex', '1');

    this.imgEl = this.querySelector('.panel-gallery-pic');

    this.leftNavButton.addEventListener('click', () => this.scrollNavBy(-1));
    this.rightNavButton.addEventListener('click', () => this.scrollNavBy(1));

    this.navEl = this.querySelector('.panel-gallery-navitems');
    this.navEl.addEventListener('click', ev => {
      let navitem = ev.target.closest('.panel-gallery-navitem');
      let picIdx = Array.from(this.navEl.children).indexOf(navitem);
      if (picIdx != -1) {
        this.setPic(picIdx, 'from-user-input');
      }
    });

    this.closeButton.onclick = () => this.close('notify');

    if (!this.sizezable) {
      this.maximizeButton.hidden = true;
    }
    this.maximizeButton.onclick = () =>
      this.maximized ? this.minimize('notify') : this.maximize('notify');

    if (this.#pics) {
      this.setImpl();
    }
  }

  connectedCallback() {
    if (this.isInitialized) {
      // If the element was reconnected because of changged current pic, then
      // restore the last scroll offset.
      if (this.lastNavScrollOfset) {
        this.scrollPicIntoView(this.#picIndex, this.lastNavScrollOfset);
      }
    }
    super.connectedCallback();
  }

  static get markup() {
    const arrow =
      'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNiIgaGVpZ2h0PSIyNiI+CiAgICA8ZyBmaWxsPSJub25lIj4KICAgICAgICA8Y2lyY2xlIGN4PSIxMyIgY3k9IjEzIiByPSIxMiIgZmlsbD0iIzAwMCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii41Ii8+CiAgICAgICAgPHBhdGggZmlsbD0iI2ZmZiIgZD0iTTEyLjU2NSAxNi44MjJjLjEyNi4xMi4yNy4xODIuNDM2LjE4Mi4xNjggMCAuMzEtLjA2LjQzLS4xODJsNC4zOC00LjE3NWMuMTI4LS4xMi4xOS0uMjYyLjE5LS40MjQgMC0uMTYtLjA2Mi0uMzAyLS4xOS0uNDIzbC0uNjUtLjYyMmMtLjEyNS0uMTItLjI3Mi0uMTgyLS40NC0uMTgyLS4xNyAwLS4zMTQuMDYtLjQzNi4xODJsLTMuMzE0IDMuMTYtMy4yNS0zLjE2Yy0uMTI2LS4xMi0uMjctLjE4Mi0uNDMtLjE4Mi0uMTY2IDAtLjMxLjA2LS40MzUuMTgybC0uNjYyLjYyMmMtLjEyNi4xMi0uMTkuMjYyLS4xOS40MjMgMCAuMTYyLjA2NC4zMDMuMTkuNDI0bDQuMzczIDQuMTc1eiIvPgogICAgPC9nPgo8L3N2Zz4=';

    return `
      <div class='image-slider-buttons'>
        <img src='/images/maximize-window.png' class='image-slider-maximize'
             title='Maximize'>
        <image-close-button></<image-close-button>
      </div>
      <img class='panel-gallery-pic'>
      <div class='loader blasting-ripple'></div>
      <div class='panel-gallery-nav'>
        <img src='${arrow}' class='panel-gallery-navleft'>
        <div class='panel-gallery-navitems'></div>
        <img src='${arrow}' class='panel-gallery-navright'>
      </div>
    `;
  }

  setImpl() {
    this.navEl.innerHTML = Array(this.picCount)
      .fill(`<div class='panel-gallery-navitem'><div></div></div>`)
      .join('');
    this.imgEls = Array(this.picCount);

    this.setPic(this.#picIndex);
    this.refresh();
  }

  setNavItemClass(picIdx, cn) {
    this.navEl.children[picIdx].classList.add(cn);
  }

  close(notify) {
    if (notify && this.onclose) {
      this.onclose();
    }
  }

  maximize(notify) {
    this.maximized = true;
    if (notify && this.onmaximize) {
      this.onmaximize();
    }
  }

  minimize(notify) {
    this.maximized = false;
    if (notify && this.onminimize) {
      this.onminimize();
    }
  }

  get picCount() {
    return this.#pics.length;
  }

  async setPic(picIdx, fromUserInput) {
    // Do not load more than one image at a time.
    this.#picIdxToLoad = picIdx;
    if (this.#loadPicPromise) {
      return;
    }

    // Start image loading.
    this.#loadPicPromise = this.loadPicAt(picIdx);
    if (!this.#loadPicPromise) {
      return;
    }

    animateUILoading(this.imgEl, this.#loadPicPromise);

    let img = await this.#loadPicPromise;
    this.#loadPicPromise = null;

    // Do the latest setPic only.
    if (picIdx != this.#picIdxToLoad) {
      return this.setPic(this.#picIdxToLoad);
    }

    this.imgEl.parentNode.replaceChild(img, this.imgEl);
    this.imgEl = img;

    let prevItem = this.navEl.querySelector('[current]');
    if (prevItem) {
      prevItem.removeAttribute('current');
    }
    let item = this.navEl.children[picIdx];

    item.setAttribute('current', 'true');

    this.scrollPicIntoView(picIdx);
    this.#picIndex = picIdx;

    if (this.onchange) {
      this.lastNavScrollOfset = this.navEl.scrollLeft;
      this.onchange(picIdx, fromUserInput);
    }
  }

  setPicBy(offset, fromUserInput) {
    let count = this.picCount;
    let nextIdx = (this.#picIndex + offset + count) % count;
    log(
      `[control:image-slider] set pic at ${offset} offset, pic index: ${nextIdx}`
    );
    this.setPic(nextIdx, fromUserInput);
  }

  scrollNavBy(offset) {
    this.navEl.scrollLeft += offset * this.visiblePicCount * this.navItemWidth;
    this.loadVisiblePics(); // load async
  }

  async loadVisiblePics() {
    let firstIdx = this.firstVisiblePicIndex;
    let lastIdx = firstIdx + this.visiblePicCount - 1;
    log(
      `[control:image-slider] load visible pics between ${firstIdx} and ${lastIdx} indices`
    );
    for (let idx = firstIdx; idx <= lastIdx; idx++) {
      // Navbar was scrolled, cancel pics loading.
      if (firstIdx != this.firstVisiblePicIndex) {
        break;
      }
      await this.loadPicAt(idx);
    }
  }

  async loadPicAt(idx) {
    if (!this.imgEls[idx]) {
      let url = await this.#pics[idx];
      if (!url) {
        error(
          `[control:image-slider] no pic URL at ${idx} index in ${this.#pics
            .map(p => `'${p}'`)
            .join(',')} picture list`
        );
        return null;
      }
      this.imgEls[idx] = new Promise(resolve => {
        let img = document.createElement('img');
        img.className = 'panel-gallery-pic';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(img);
        img.src = url;
        log(`[control:image-slider] pic loaded at index: ${idx}`);
      });
    }
    return this.imgEls[idx];
  }

  scrollPicIntoView(picIdx, wantedScroll) {
    // Scroll the item into the middle of the navbar if the item is out of screen.
    if (
      picIdx < this.firstVisiblePicIndex ||
      picIdx > this.firstVisiblePicIndex + this.visiblePicCount
    ) {
      let navItemWidth = this.navItemWidth;
      if (
        wantedScroll &&
        picIdx * navItemWidth >= wantedScroll &&
        picIdx * navItemWidth <=
          wantedScroll + this.visiblePicCount * navItemWidth
      ) {
        this.navEl.scrollLeft = wantedScroll;
      } else {
        this.navEl.scrollLeft =
          (picIdx - this.visiblePicCount / 2) * this.navItemWidth;
      }
    }
  }

  get navControlWidth() {
    // Use hardcoded value because the control is not visible,
    // and cannot be computed.
    return 32;
  }

  get navItemWidth() {
    return this.#getWidthOf(this.navEl.firstElementChild);
  }

  get firstVisiblePicIndex() {
    return parseInt(this.navEl.scrollLeft / this.navItemWidth);
  }

  static selectors = {
    CloseButton: 'image-close-button',
    MaximizeButton: '.image-slider-maximize',
    LeftNavButton: '.panel-gallery-navleft',
    RightNavButton: '.panel-gallery-navright',
    Pic: '.panel-gallery-pic',

    NavItemAt: i => `.panel-gallery-navitem:nth-child(${i + 1})`,
  };

  static elements = {
    closeButton: this.selectors.CloseButton,
    maximizeButton: this.selectors.MaximizeButton,
    leftNavButton: this.selectors.LeftNavButton,
    rightNavButton: this.selectors.RightNavButton,
  };

  #getWidthOf(el) {
    const width = el.getBoundingClientRect().width;
    const styles = window.getComputedStyle(el);
    const margin =
      parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
    return width + margin;
  }

  // A list of picture URLs or promises resolved to URLs.
  #pics;

  // An index of a currently selected picture.
  #picIndex;

  #picIdxToLoad;
  #loadPicPromise;
}

window.customElements.define('image-slider', ImageSlider);

export { ImageSlider };
